import { useState, useEffect, createContext, useMemo, useContext } from 'react'
import PropTypes from 'prop-types'
import { useDispatch } from 'react-redux'
import { redFunnelAPI } from '../api/api'
import { logout } from '../redux/authentication'
import Modal from '../components/Modal'

// minutes * seconds * milliseconds
const sessionLengthInMilliseconds = 20 * 60 * 1000 // 1200000 aka 20 mins
const sessionWarningInMilliseconds = 18 * 60 * 1000 // 1080000 aka 18 mins

const msToHoursAndMinutes = duration => {
  let seconds = Math.floor((duration / 1000) % 60)
  let minutes = Math.floor((duration / (1000 * 60)) % 60)

  minutes = minutes < 10 ? `0${minutes}` : minutes
  seconds = seconds < 10 ? `0${seconds}` : seconds

  return `${minutes}:${seconds}`
}

const SessionWarningContext = createContext(null)

export const SessionWarningProvider = ({ children }) => {
  const dispatch = useDispatch()

  const [sessionModalState, setSessionModalState] = useState({
    isOpen: false,
    hasExpired: false,
    timeLeft: null,
  })

  useEffect(() => {
    const checkSession = () => {
      const lastHit = redFunnelAPI.getLastSessionHit()
      const now = new Date().getTime()
      const timeLeft = lastHit + sessionLengthInMilliseconds - now
      if (
        lastHit + sessionLengthInMilliseconds - now <= 0 &&
        !sessionModalState.hasExpired
      ) {
        handleSessionEnd()
      } else if (lastHit + sessionWarningInMilliseconds - now <= 0) {
        setSessionModalState({
          isOpen: true,
          hasExpired: false,
          timeLeft,
        })
      }
    }
    let timer

    if (!sessionModalState.hasExpired) {
      timer = setInterval(() => checkSession(), 1000)
    }

    return () => clearInterval(timer)
  }, [sessionModalState]) // eslint-disable-line

  const handleSessionEnd = () => {
    dispatch(logout())
    setSessionModalState({
      isOpen: true,
      hasExpired: true,
    })
  }

  const handleAcknowledgeExpiredSession = () => {
    redFunnelAPI.startNewSession()
    setSessionModalState({
      isOpen: false,
      hasExpired: false,
    })
  }

  const handleKeepAlive = () => {
    redFunnelAPI.getTravelCardPrices({})
    setSessionModalState({
      isOpen: false,
      hasExpired: false,
    })
  }

  const value = useMemo(
    () => ({
      sessionIsExpired: sessionModalState.hasExpired,
    }),
    [sessionModalState.hasExpired],
  )

  return (
    <SessionWarningContext.Provider value={value}>
      {children}
      <Modal isOpen={sessionModalState.isOpen} className="max-sm:w-8/12">
        <div className="bg-white p-8">
          {sessionModalState?.hasExpired ||
          (sessionModalState.timeLeft && sessionModalState.timeLeft < 0) ? (
            <div className="text-center ">
              <h3 className="mb-8 uppercase text-red">
                Your Session has expired
              </h3>
              <p className="mb-8">
                Due to an extended period of inactivity, your session was
                automatically closed.
              </p>
              <button
                onClick={handleAcknowledgeExpiredSession}
                type="button"
                className="btn btn-primary w-1/2"
              >
                Ok
              </button>
            </div>
          ) : (
            <div className="text-center ">
              <h3 className="mb-8 uppercase text-red">Extend your Session</h3>
              <p className="mb-8">
                Your session will expire in{' '}
                {msToHoursAndMinutes(Math.floor(sessionModalState.timeLeft))}
              </p>
              <button
                onClick={handleKeepAlive}
                type="button"
                className="btn btn-primary w-1/2 min-w-[100%] xxs:min-w-[155px]"
              >
                Extend My Session
              </button>
            </div>
          )}
        </div>
      </Modal>
    </SessionWarningContext.Provider>
  )
}

SessionWarningProvider.propTypes = {
  children: PropTypes.node.isRequired,
}
const useSessionContext = () => useContext(SessionWarningContext)
export default useSessionContext
